import html2canvas from 'html2canvas';

// Helper function to get state background color
const getStateColor = (stateCode) => {
  switch(stateCode) {
    case 'VIC': return '#64748b';
    case 'NSW': return '#0369a1';
    case 'ACT': return '#2563eb';
    case 'QLD': return '#be123c';
    case 'SA': return '#dc2626';
    case 'NT': return '#ea580c';
    case 'WA': return '#b45309';
    case 'TAS': return '#15803d';
    default: return '#0f172a';
  }
};

// Helper function to get event type background color
const getEventTypeColor = (eventType) => {
  switch(eventType) {
    case 'Beginner': return { bg: '#bfdbfe', text: '#1e40af' };
    case 'Practice': return { bg: '#bbf7d0', text: '#166534' };
    case 'Competition': return { bg: '#fed7aa', text: '#9a3412' };
    case 'Exhibition': return { bg: '#fecaca', text: '#991b1b' };
    default: return { bg: '#e5e7eb', text: '#374151' };
  }
};

// Format date/time for display
const formatDateTime = (dateString) => {
  try {
    const [datePart, timePart] = dateString.split('T');
    const [fullYear, month, day] = datePart.split('-');
    const [hours, minutes] = timePart.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'pm' : 'am';
    const hour12 = hour % 12 || 12;
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                       'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthName = monthNames[parseInt(month) - 1];
    return `${day}-${monthName}-${fullYear.slice(-2)}, ${hour12}:${minutes.padStart(2, '0')} ${ampm}`;
  } catch (error) {
    return dateString;
  }
};

// Render card style (for < 9 events) - matches EventCardView
const renderCardStyle = (event, eventCount) => {
  const [datePart] = event.StartDateTime.split('T');
  const [, month, day] = datePart.split('-');
  const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 
                     'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  const monthDisplay = monthNames[parseInt(month) - 1];
  
  // Determine card size based on event count - larger cards for fewer events
  const isSingleCard = eventCount === 1;
  const isLargeCards = eventCount <= 3;
  const isMediumCards = eventCount <= 6;
  
  const cardHeight = isSingleCard ? '320px' : isLargeCards ? '300px' : isMediumCards ? '280px' : '260px';
  const cardWidth = isSingleCard ? '400px' : isLargeCards ? '320px' : isMediumCards ? '280px' : '260px';
  const dateBadgeSize = isSingleCard ? '80px' : isLargeCards ? '72px' : isMediumCards ? '64px' : '56px';
  const dateDaySize = isSingleCard ? '44px' : isLargeCards ? '40px' : isMediumCards ? '36px' : '32px';
  const dateMonthSize = isSingleCard ? '18px' : isLargeCards ? '16px' : isMediumCards ? '14px' : '12px';
  const imageHeight = isSingleCard ? '100px' : isLargeCards ? '90px' : isMediumCards ? '85px' : '80px';
  const titleSize = isSingleCard ? '18px' : isLargeCards ? '16px' : isMediumCards ? '15px' : '14px';
  const textSize = isSingleCard ? '13px' : isLargeCards ? '12px' : isMediumCards ? '11px' : '11px';
  const tagSize = isSingleCard ? '11px' : isLargeCards ? '10px' : isMediumCards ? '10px' : '9px';
  const padding = isSingleCard ? '24px' : isLargeCards ? '20px' : isMediumCards ? '16px' : '12px';
  
  const eventCard = document.createElement('div');
  eventCard.style.position = 'relative';
  eventCard.style.background = 'white';
  eventCard.style.borderRadius = '12px';
  eventCard.style.overflow = 'hidden';
  eventCard.style.height = cardHeight;
  eventCard.style.width = cardWidth;
  eventCard.style.boxSizing = 'border-box';
  eventCard.style.display = 'flex';
  eventCard.style.flexDirection = 'column';
  eventCard.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';

  // Date badge (absolute positioned top-left)
  const dateBadge = document.createElement('div');
  const dateBadgeSpacing = isSingleCard ? '16px' : isLargeCards ? '14px' : isMediumCards ? '12px' : '10px';
  dateBadge.style.position = 'absolute';
  dateBadge.style.top = dateBadgeSpacing;
  dateBadge.style.left = dateBadgeSpacing;
  dateBadge.style.width = dateBadgeSize;
  dateBadge.style.background = 'rgba(15, 23, 42, 0.9)';
  dateBadge.style.color = 'white';
  dateBadge.style.borderRadius = '8px';
  dateBadge.style.overflow = 'hidden';
  dateBadge.style.zIndex = '10';
  dateBadge.style.textAlign = 'center';
  dateBadge.innerHTML = `
    <div style="font-size: ${dateDaySize}; font-weight: bold; padding: 4px 0 2px 0;">${parseInt(day)}</div>
    <div style="font-size: ${dateMonthSize}; background: rgba(15, 23, 42, 0.6); padding: 2px 0 4px 0;">${monthDisplay}</div>
  `;
  eventCard.appendChild(dateBadge);

  // Image section
  const imageSection = document.createElement('div');
  imageSection.style.height = imageHeight;
  imageSection.style.background = 'linear-gradient(to bottom right, #475569, #1e293b)';
  imageSection.style.overflow = 'hidden';
  imageSection.style.position = 'relative';
  imageSection.style.flexShrink = '0';
  
  // Fallback text for when image fails to load
  const fallbackText = document.createElement('div');
  fallbackText.style.position = 'absolute';
  fallbackText.style.inset = '0';
  fallbackText.style.display = 'flex';
  fallbackText.style.alignItems = 'center';
  fallbackText.style.justifyContent = 'center';
  fallbackText.style.color = 'white';
  fallbackText.style.fontSize = isSingleCard ? '18px' : isLargeCards ? '16px' : isMediumCards ? '14px' : '12px';
  fallbackText.style.fontWeight = '600';
  fallbackText.style.textAlign = 'center';
  fallbackText.style.padding = '0 20px';
  fallbackText.style.opacity = '0';
  fallbackText.style.transition = 'opacity 0.3s';
  fallbackText.textContent = event.BusinessName || '';
  imageSection.appendChild(fallbackText);
  
  if (event.organiserImage) {
    const imgWrapper = document.createElement('div');
    imgWrapper.style.position = 'absolute';
    imgWrapper.style.inset = '0';
    imgWrapper.style.display = 'flex';
    imgWrapper.style.alignItems = 'center';
    imgWrapper.style.justifyContent = 'center';
    const img = document.createElement('img');
    img.crossOrigin = 'anonymous';
    img.style.width = '280px';
    img.style.height = 'auto';
    img.style.objectFit = 'contain';
    img.style.transform = 'rotate(-12deg) scale(1.2)';
    
    // Set up error handling before setting src to catch errors immediately
    let errorHandled = false;
    const handleError = () => {
      if (!errorHandled) {
        errorHandled = true;
        img.style.display = 'none';
        img.style.visibility = 'hidden';
        fallbackText.style.opacity = '1';
      }
    };
    
    img.onerror = handleError;
    img.onabort = handleError;
    
    // If image loads successfully, ensure fallback is hidden
    img.onload = () => {
      if (!errorHandled) {
        fallbackText.style.opacity = '0';
      }
    };
    
    // Try to load image, but catch any immediate errors
    try {
      img.src = event.organiserImage;
      // Set a timeout to handle images that never load
      setTimeout(() => {
        if (!img.complete || img.naturalHeight === 0) {
          handleError();
        }
      }, 2000);
    } catch (error) {
      handleError();
    }
    
    imgWrapper.appendChild(img);
    imageSection.appendChild(imgWrapper);
  } else {
    // No image provided, show promoter name
    fallbackText.style.opacity = '1';
  }
  eventCard.appendChild(imageSection);

  // Content section
  const contentSection = document.createElement('div');
  contentSection.style.flex = '1';
  contentSection.style.background = 'white';
  contentSection.style.display = 'flex';
  contentSection.style.flexDirection = 'column';
  const bottomPadding = isSingleCard ? '28px' : isLargeCards ? '24px' : isMediumCards ? '20px' : '18px';
  contentSection.style.padding = `${padding} ${padding} ${bottomPadding} ${padding}`;
  contentSection.style.overflow = 'auto';
  contentSection.style.minHeight = '0';
  contentSection.style.boxSizing = 'border-box';

  // Tags row
  const tagsRow = document.createElement('div');
  tagsRow.style.display = 'flex';
  tagsRow.style.gap = isSingleCard ? '12px' : isLargeCards ? '10px' : isMediumCards ? '8px' : '6px';
  tagsRow.style.marginBottom = isSingleCard ? '14px' : isLargeCards ? '12px' : isMediumCards ? '10px' : '8px';

  // State badge - no background, use state color as text color
  const stateBadge = document.createElement('span');
  stateBadge.style.display = 'inline';
  stateBadge.style.fontSize = tagSize;
  stateBadge.style.fontWeight = '500';
  stateBadge.style.color = getStateColor(event.StateNameShort);
  stateBadge.style.lineHeight = '1';
  stateBadge.textContent = event.StateNameShort;
  tagsRow.appendChild(stateBadge);

  // Event type badge - no background, use event type text color
  const eventTypeColors = getEventTypeColor(event.EventType);
  const eventTypeBadge = document.createElement('span');
  eventTypeBadge.style.display = 'inline';
  eventTypeBadge.style.fontSize = tagSize;
  eventTypeBadge.style.fontWeight = '500';
  eventTypeBadge.style.color = eventTypeColors.text;
  eventTypeBadge.style.lineHeight = '1';
  eventTypeBadge.textContent = event.EventType;
  tagsRow.appendChild(eventTypeBadge);
  contentSection.appendChild(tagsRow);

  // Event name
  const eventName = document.createElement('h3');
  eventName.style.fontSize = titleSize;
  eventName.style.fontWeight = '600';
  eventName.style.color = '#111827';
  eventName.style.margin = '0 0 10px 0';
  eventName.style.lineHeight = '1.4';
  eventName.style.wordWrap = 'break-word';
  eventName.style.overflowWrap = 'break-word';
  eventName.textContent = event.EventType === 'Competition' && event.RoundNum
    ? `${event.EventName}, Round ${event.RoundNum}`
    : event.EventName;
  contentSection.appendChild(eventName);

  // Venue
  if (event.VenueName) {
    const venue = document.createElement('div');
    venue.style.fontSize = textSize;
    venue.style.color = '#111827';
    venue.style.marginBottom = '8px';
    venue.style.lineHeight = '1.5';
    venue.style.wordWrap = 'break-word';
    venue.style.overflowWrap = 'break-word';
    venue.textContent = event.VenueName;
    contentSection.appendChild(venue);
  }

  // Layout
  if (event.LayoutName) {
    const layout = document.createElement('div');
    layout.style.fontSize = textSize;
    layout.style.color = '#6b7280';
    layout.style.marginBottom = '10px';
    layout.style.lineHeight = '1.5';
    layout.style.wordWrap = 'break-word';
    layout.style.overflowWrap = 'break-word';
    layout.textContent = event.LayoutName;
    contentSection.appendChild(layout);
  }

  // Times
  const startTime = document.createElement('p');
  startTime.style.fontSize = textSize;
  startTime.style.color = '#4b5563';
  startTime.style.margin = '0 0 6px 0';
  startTime.style.lineHeight = '1.5';
  startTime.style.wordWrap = 'break-word';
  startTime.style.overflowWrap = 'break-word';
  startTime.textContent = `Start: ${formatDateTime(event.StartDateTime)}`;
  contentSection.appendChild(startTime);

  const endTime = document.createElement('p');
  endTime.style.fontSize = textSize;
  endTime.style.color = '#4b5563';
  endTime.style.margin = '0';
  endTime.style.lineHeight = '1.5';
  endTime.style.wordWrap = 'break-word';
  endTime.style.overflowWrap = 'break-word';
  endTime.textContent = `End: ${formatDateTime(event.EndDateTime)}`;
  contentSection.appendChild(endTime);

  eventCard.appendChild(contentSection);
  return eventCard;
};

// Render compact style (for 10+ events) - responsive compact layout
const renderCompactStyle = (event) => {
  const eventCard = document.createElement('div');
  eventCard.style.background = 'white';
  eventCard.style.borderRadius = '8px';
  eventCard.style.padding = '8px';
  eventCard.style.display = 'flex';
  eventCard.style.gap = '8px';
  eventCard.style.alignItems = 'flex-start';
  eventCard.style.boxSizing = 'border-box';
  eventCard.style.overflow = 'hidden';

  // Date column
  const dateColumn = document.createElement('div');
  dateColumn.style.display = 'flex';
  dateColumn.style.flexDirection = 'column';
  dateColumn.style.gap = '4px';
  dateColumn.style.flexShrink = '0';

  // Date box
  const dateBox = document.createElement('div');
  dateBox.style.background = '#0f172a';
  dateBox.style.color = 'white';
  dateBox.style.padding = '4px 6px';
  dateBox.style.borderRadius = '6px';
  dateBox.style.textAlign = 'center';
  dateBox.style.width = '42px';
  
  const eventDate = new Date(event.StartDateTime);
  const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

  dateBox.innerHTML = `
    <div style="font-size: 16px; font-weight: bold; line-height: 1;">${eventDate.getUTCDate()}</div>
    <div style="font-size: 10px;">${monthNames[eventDate.getUTCMonth()]}</div>
  `;
  dateColumn.appendChild(dateBox);

  // State badge - colored text only, below date
  const stateBadge = document.createElement('div');
  stateBadge.style.fontSize = '10px';
  stateBadge.style.fontWeight = '500';
  stateBadge.style.color = getStateColor(event.StateNameShort);
  stateBadge.style.lineHeight = '1';
  stateBadge.style.textAlign = 'center';
  stateBadge.textContent = event.StateNameShort;
  dateColumn.appendChild(stateBadge);

  // Details column
  const detailsColumn = document.createElement('div');
  detailsColumn.style.flex = '1';
  detailsColumn.style.display = 'flex';
  detailsColumn.style.flexDirection = 'column';
  detailsColumn.style.gap = '4px';
  detailsColumn.style.minWidth = '0';

  // Event Type (colored text, no background)
  const eventTypeColors = getEventTypeColor(event.EventType);
  const eventTypeBadge = document.createElement('div');
  eventTypeBadge.style.fontSize = '10px';
  eventTypeBadge.style.fontWeight = '500';
  eventTypeBadge.style.color = eventTypeColors.text;
  eventTypeBadge.style.lineHeight = '1';
  eventTypeBadge.style.marginBottom = '2px';
  eventTypeBadge.textContent = event.EventType;
  detailsColumn.appendChild(eventTypeBadge);

  // Event name
  const eventName = document.createElement('div');
  eventName.style.fontSize = '12px';
  eventName.style.fontWeight = '600';
  eventName.style.color = '#0f172a';
  eventName.style.lineHeight = '1.4';
  eventName.style.marginBottom = '4px';
  eventName.style.wordWrap = 'break-word';
  eventName.style.overflowWrap = 'break-word';
  eventName.textContent = event.EventType === 'Competition' && event.RoundNum
    ? `${event.EventName} - Round ${event.RoundNum}`
    : event.EventName;
  detailsColumn.appendChild(eventName);

  // Promoter
  if (event.BusinessName) {
    const promoter = document.createElement('div');
    promoter.style.color = '#64748b';
    promoter.style.fontSize = '10px';
    promoter.style.lineHeight = '1.4';
    promoter.style.marginBottom = '2px';
    promoter.style.wordWrap = 'break-word';
    promoter.style.overflowWrap = 'break-word';
    promoter.textContent = event.BusinessName;
    detailsColumn.appendChild(promoter);
  }

  // Venue
  if (event.VenueName) {
    const venue = document.createElement('div');
    venue.style.color = '#64748b';
    venue.style.fontSize = '10px';
    venue.style.lineHeight = '1.4';
    venue.style.wordWrap = 'break-word';
    venue.style.overflowWrap = 'break-word';
    venue.textContent = event.VenueName;
    detailsColumn.appendChild(venue);
  }

  eventCard.appendChild(dateColumn);
  eventCard.appendChild(detailsColumn);
  return eventCard;
};

export const generateEventImage = async (monthName, events) => {
  // Create a temporary container
  const container = document.createElement('div');
  container.style.width = '1080px';
  container.style.height = '1080px';
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '-9999px';
  container.style.background = 'linear-gradient(to bottom, #1e293b, #0f172a)';
  container.style.padding = '10px 40px';
  container.style.boxSizing = 'border-box';
  container.style.fontFamily = 'Inter, sans-serif';

  // Add background overlay
  const bgOverlay = document.createElement('div');
  bgOverlay.style.position = 'absolute';
  bgOverlay.style.inset = '0';
  bgOverlay.style.backgroundImage = 'url(/driftcal_1000_black.png)';
  bgOverlay.style.backgroundSize = 'cover';
  bgOverlay.style.backgroundPosition = 'center';
  bgOverlay.style.opacity = '0.1';
  bgOverlay.style.transform = 'scale(1.5)';
  container.appendChild(bgOverlay);

  // Content wrapper - simplified layout
  const content = document.createElement('div');
  content.style.position = 'relative';
  content.style.zIndex = '1';
  content.style.height = '100%';
  content.style.display = 'flex';
  content.style.flexDirection = 'column';
  content.style.paddingTop = '20px';
  content.style.paddingBottom = '30px';
  content.style.boxSizing = 'border-box';

  // Add title - minimal spacing
  const title = document.createElement('h1');
  title.style.color = 'white';
  title.style.fontSize = '24px';
  title.style.fontWeight = 'bold';
  title.style.textAlign = 'center';
  title.style.margin = '0 0 20px 0';
  title.style.flexShrink = '0';
  title.textContent = 'My Saved Events';
  content.appendChild(title);

  // Determine layout based on event count
  const eventCount = events.length;
  const useCardStyle = eventCount < 9;
  
  // Calculate optimal grid layout for card style
  const calculateCardGrid = (count) => {
    let columns, rows, gap;
    
    if (count === 1) {
      columns = 1;
      rows = 1;
      gap = '0';
    } else if (count === 2) {
      columns = 2;
      rows = 1;
      gap = '24px';
    } else if (count === 3) {
      columns = 3;
      rows = 1;
      gap = '24px';
    } else if (count === 4) {
      columns = 2;
      rows = 2;
      gap = '20px';
    } else if (count === 5) {
      columns = 3;
      rows = 2;
      gap = '20px';
    } else if (count === 6) {
      columns = 2;
      rows = 3;
      gap = '20px';
    } else if (count === 7) {
      columns = 3;
      rows = 3;
      gap = '16px';
    } else if (count === 8) {
      columns = 3;
      rows = 3;
      gap = '16px';
    } else { // 9
      columns = 3;
      rows = 3;
      gap = '16px';
    }
    
    return { columns, rows, gap };
  };
  
  // Events container - centered vertically with flex
  const eventsContainer = document.createElement('div');
  eventsContainer.style.flex = '1';
  eventsContainer.style.display = 'flex';
  eventsContainer.style.alignItems = 'center';
  eventsContainer.style.justifyContent = 'center';
  eventsContainer.style.minHeight = '0';
  eventsContainer.style.overflow = 'hidden';
  
  // Add events list
  const eventsList = document.createElement('div');
  eventsList.style.display = 'grid';
  eventsList.style.width = '100%';
  eventsList.style.boxSizing = 'border-box';
  
  if (useCardStyle) {
    const gridLayout = calculateCardGrid(eventCount);
    eventsList.style.gridTemplateColumns = `repeat(${gridLayout.columns}, 1fr)`;
    eventsList.style.gridTemplateRows = `repeat(${gridLayout.rows}, auto)`;
    eventsList.style.gap = gridLayout.gap;
    eventsList.style.justifyItems = 'center';
    eventsList.style.alignItems = 'center';
    
    // For single item, center it with auto width
    if (eventCount === 1) {
      eventsList.style.width = 'auto';
      eventsList.style.maxWidth = 'none';
    } else {
      eventsList.style.maxWidth = '1000px';
      eventsList.style.margin = '0 auto';
    }
  } else {
    // Compact style: Adaptive columns based on event count
    let columns, rowGap, columnGap;
    if (eventCount <= 15) {
      columns = 4;
    } else if (eventCount <= 25) {
      columns = 5;
    } else {
      columns = 6;
    }
    
    // Calculate number of rows
    const rows = Math.ceil(eventCount / columns);
    
    // Adjust row gap based on number of rows - fewer rows = more spacing, more rows = less spacing
    if (rows === 1) {
      rowGap = '0px';
      columnGap = '8px';
    } else if (rows === 2) {
      rowGap = '40px';
      columnGap = '8px';
    } else if (rows === 3) {
      rowGap = '30px';
      columnGap = '6px';
    } else if (rows === 4) {
      rowGap = '20px';
      columnGap = '6px';
    } else if (rows === 5) {
      rowGap = '16px';
      columnGap = '6px';
    } else {
      rowGap = '12px';
      columnGap = '6px';
    }
    
    eventsList.style.gridTemplateColumns = `repeat(${columns}, 1fr)`;
    eventsList.style.gridAutoRows = 'auto';
    eventsList.style.rowGap = rowGap;
    eventsList.style.columnGap = columnGap;
    eventsList.style.maxWidth = '1000px';
    eventsList.style.margin = 'auto';
    eventsList.style.alignSelf = 'center';
  }
  
  // Render events
  events.forEach(event => {
    const eventCard = useCardStyle 
      ? renderCardStyle(event, eventCount)
      : renderCompactStyle(event);
    eventsList.appendChild(eventCard);
  });

  eventsContainer.appendChild(eventsList);
  content.appendChild(eventsContainer);

  // Add footer - at bottom
  const footer = document.createElement('div');
  footer.style.marginTop = 'auto';
  footer.style.color = 'rgba(255, 255, 255, 0.7)';
  footer.style.fontSize = '12px';
  footer.style.textAlign = 'center';
  footer.style.flexShrink = '0';
  footer.style.paddingTop = '10px';
  footer.textContent = 'Generated by DriftCal.net';
  content.appendChild(footer);

  container.appendChild(content);
  document.body.appendChild(container);

  // Wait for images to load or fail before rendering (reduces CORS errors in console)
  await new Promise(resolve => setTimeout(resolve, 1000));

  try {
    const canvas = await html2canvas(container, {
      width: 1080,
      height: 1080,
      scale: 2,
      logging: false,
      useCORS: true,
      allowTaint: false,
      ignoreElements: (element) => {
        // Ignore images that failed to load or are hidden
        if (element.tagName === 'IMG') {
          return element.style.display === 'none' || 
                 element.style.visibility === 'hidden' ||
                 (!element.complete && element.naturalHeight === 0);
        }
        return false;
      },
      onclone: (clonedDoc) => {
        // Handle any remaining CORS issues in cloned document
        const images = clonedDoc.querySelectorAll('img');
        images.forEach(img => {
          if (!img.complete || img.naturalHeight === 0) {
            img.style.display = 'none';
            img.style.visibility = 'hidden';
          }
        });
      }
    });
    
    const image = canvas.toDataURL('image/jpeg', 0.9);
    document.body.removeChild(container);
    return image;
  } catch (error) {
    console.error('Error generating image:', error);
    document.body.removeChild(container);
    throw error;
  }
};
